/**
 * Landing surface: human-readable marketing copy (headline + value props) that
 * lives in the DOM for crawlers, LLMs, and social unfurlers to read. The
 * client-rendered SPA otherwise serves an empty <div id="root">.
 *
 * Rendered client-side today. To fully capture the SEO value, a future build
 * step should prerender this markup into the static index.html so it ships in
 * the initial HTML (React then hydrates over it); the rendered UX is identical
 * either way.
 *
 * Visibility is owned by App (header toggle + "start collapsed when the user
 * already has entries"); this component is purely presentational.
 */

import { Button, Card, Badge } from "@lazar/ui";

const FEATURES: { icon: string; title: string; body: string }[] = [
  {
    icon: "🍋",
    title: "Local-first & yours",
    body: "Your entries live in your browser, never uploaded. Export to JSON or CSV anytime. Your data is yours to keep.",
  },
  {
    icon: "⚡",
    title: "No account, no setup",
    body: "Open the page and start the clock. There's nothing to sign up for and nothing to install.",
  },
  {
    icon: "🗂️",
    title: "Clients & projects",
    body: "Tag every slice of time to the client and project it belongs to, organized the way you bill.",
  },
  {
    icon: "📊",
    title: "Analytics that pace you",
    body: "Weekly summaries and charts against a workweek you set, so you always know where the week stands.",
  },
];

interface LandingProps {
  /** Dismiss the landing and scroll focus to the tracker. */
  onStart: () => void;
  /** Dismiss the landing without moving the viewport. */
  onClose: () => void;
}

export default function Landing({ onStart, onClose }: LandingProps) {
  return (
    <section
      aria-labelledby="landing-headline"
      className="mx-auto w-full max-w-7xl px-4 pt-8 sm:px-6 lg:px-8"
    >
      <Card className="p-8 sm:p-12">
        {/* Hero */}
        <div className="max-w-2xl">
          <Badge className="bg-lemon-500 text-ink mb-5 inline-flex">
            local-first · no account
          </Badge>
          <h2
            id="landing-headline"
            className="text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl"
          >
            Time tracking that stays on your laptop.
          </h2>
          <p className="mt-5 max-w-xl text-lg opacity-80">
            Lemon Pi is a local-first time tracker. No account, no cloud, no one
            watching. Start a timer, tag it to a client, and watch the slices
            add up.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              onClick={onStart}
              variant="primary"
              className="text-base"
            >
              Start tracking →
            </Button>
            <a
              href="https://github.com/mauricedesaxe/time-tracker"
              target="_blank"
              rel="noopener noreferrer"
              className="brut-btn text-base"
            >
              See it on GitHub
            </a>
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium underline opacity-60 hover:opacity-100"
            >
              skip intro
            </button>
          </div>
        </div>

        {/* Seam between the pitch and the value props */}
        <div className="brut-divider mt-10 mb-8" />

        {/* Value props: plain blocks now, held inside the one big card */}
        <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <div key={f.title}>
              <div className="text-2xl" aria-hidden="true">
                {f.icon}
              </div>
              <h3 className="mt-2 font-black">{f.title}</h3>
              <p className="mt-1 text-sm opacity-75">{f.body}</p>
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
}
